import {
  AfterViewInit,
  Component,
  effect,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  resolveForwardRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  AccordionDefaultState,
  cssToNgStyle,
  is2dArray,
  isLayoutWidget,
  NewUiZoneWidgetConfig,
  ProviderClass,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BrnAccordion, BrnAccordionItem } from '@spartan-ng/brain/accordion';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { AccordionStateService } from './accordion-state.service';

import { createChildWidgetInjector } from '../../zones/utils';
import { ChildComponentList } from '../../zones/widget-layout/main-widget/main-widget.component';

type AccordionConfig = {
  title: string;
  containerName: string;
  css: string;
  defaultState?: AccordionDefaultState;
};

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-accordion-widget',
  templateUrl: './accordion-widget.component.html',
  styleUrls: ['./accordion-widget.component.scss'],
})
export class AccordionWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  public injectorComponentsArrays: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  }[] = [];

  @ViewChild(BrnAccordion) private accordion!: BrnAccordion;
  @ViewChildren(BrnAccordionItem) private accordionItems!: QueryList<BrnAccordionItem>;

  public openStates: boolean[] = [];
  public mouseOver = '';
  public mouseOverId = '';
  public renderedTitles: string[] = [];
  private isWorkflowType: boolean;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public getWidgetClasses = getWidgetClasses;

  constructor(
    public childComponents: ChildComponentList,
    public dataIn: ProviderClass<object>,
    private injector: Injector,
    public configService: ConfigService,
    private store: Store,
    private readonly renderer: Renderer2,
    private readonly countriesCurrenciesService: CountriesCurrenciesService,
    private readonly iconCacheService: IconCacheService,
    private readonly accordionStateService: AccordionStateService,
  ) {}

  ngOnInit(): void {
    if (is2dArray(this.childComponents.list)) {
      const items = this.childComponents.list as NewUiZoneWidgetConfig[][];
      this.injectorComponentsArrays = Array.from(Array(items.length), () => ({}));
      items.forEach((itemComponents, index) => {
        itemComponents.forEach((widget) => {
          const component = resolveForwardRef(getComponentByLabel(widget.name));
          this.injectorComponentsArrays[index][widget.uniqueId] = {
            component,
            injector: createChildWidgetInjector(
              widget,
              this.injector,
              this.dataIn,
              isLayoutWidget(widget.name)
                ? [
                    {
                      provide: ChildComponentList,
                      useValue: new ChildComponentList(widget.childComponents ?? []),
                    },
                  ]
                : [],
            ),
            widget,
          };
        });
      });
      this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;
    } else {
      throw new Error('Accordion widget requires a 2D array of child components');
    }
    this.renderedTitles = this.accordionConfigs.map((c) => c.title);
    this.setupHandlebarTitles();
    this.restoreOpenStates();
  }

  ngAfterViewInit(): void {
    this.applyAccordionStyles();
    this.setupStateTracking();
  }

  ngOnDestroy(): void {
    this.removeAccordionStyles();
    // only clear state if we're not in workflow context, to preserve open/closed states.
    if (!this.configService.getActiveConfigType()?.isWorkflow) {
      this.accordionStateService.clearState(this.dataIn.uniqueId);
    }
  }

  private setupHandlebarTitles(): void {
    const templateFns = this.accordionConfigs.map((c) => Handlebars.compile(c.title));
    const combinedTemplate = this.accordionConfigs.map((c) => c.title).join(' ');
    buildHandleBarModel(this.store, combinedTemplate, this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedTitles = templateFns.map((fn) => filterIfHandlebarNotRendered(fn(model)));
      });
  }

  public get outerCss() {
    return cssToNgStyle(this.dataIn.widgetConfig.attributes.outerCss ?? '');
  }

  public get accordionConfigs(): AccordionConfig[] {
    return this.dataIn.widgetConfig.attributes.widgetProperties?.['accordionConfigs'] ?? [];
  }

  public get collapsedIcon(): string {
    return this.dataIn.widgetConfig.attributes.widgetProperties?.['collapsedIcon'] ?? '';
  }

  public get contentNgStyle() {
    const contentCss =
      (this.dataIn.widgetConfig.attributes.css_config?.elementStyling?.['contentCss'] as unknown as string) ??
      '';
    return cssToNgStyle(contentCss);
  }

  public getItemHeaderStyle(index: number) {
    const itemCss = this.accordionConfigs[index]?.css ?? '';
    return cssToNgStyle(itemCss);
  }

  public injectorArray(index: number) {
    return Object.values(this.injectorComponentsArrays[index] ?? {});
  }

  public changeStyle($event: any, mouseOverId?: string) {
    this.mouseOver = $event.type === 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
    $event.stopPropagation();
  }

  public selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), true));
      } else {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), false));
      }
    }
    event.stopPropagation();
  }

  private restoreOpenStates(): void {
    const savedStates = this.accordionStateService.getState(this.dataIn.uniqueId);
    const savedDefaults = this.accordionStateService.getAppliedDefaults(this.dataIn.uniqueId);
    const defaults = this.accordionConfigs.map(
      (config) => config?.defaultState === AccordionDefaultState.EXPANDED,
    );

    this.openStates = defaults.map((isDefaultOpen, i) => {
      if (savedStates?.[i] !== undefined && savedDefaults?.[i] === isDefaultOpen) {
        return savedStates[i];
      }
      return isDefaultOpen;
    });

    this.accordionStateService.setAppliedDefaults(this.dataIn.uniqueId, defaults);
  }

  private setupStateTracking(): void {
    effect(
      () => {
        const openIds = this.accordion.openItemIds();
        const items = this.accordionItems.toArray();
        const states = items.map((item) => openIds.includes(item.id));
        this.accordionStateService.setState(this.dataIn.uniqueId, states);
      },
      { injector: this.injector },
    );
  }

  private applyAccordionStyles() {
    const uniqueId = this.dataIn.uniqueId;
    const triggerSelector = `.accordion-widget-${uniqueId} .accordion-trigger-${uniqueId}`;

    const rawHeaderCss =
      (this.dataIn.widgetConfig.attributes.css_config?.elementStyling?.['headerCss'] as unknown as string) ??
      '';

    if (rawHeaderCss) {
      const transformed = rawHeaderCss
        .replace(/\.collapsed\s*\{/g, `${triggerSelector}[data-state="closed"] {`)
        .replace(/\.expanded\s*\{/g, `${triggerSelector}[data-state="open"] {`);
      this.injectStyle(`accordion-header-${uniqueId}`, transformed);
    }
  }

  private injectStyle(id: string, css: string) {
    this.removeStyle(id);
    const styleEl = this.renderer.createElement('style');
    styleEl.id = id;
    styleEl.innerHTML = css;
    document.head.append(styleEl);
  }

  private removeStyle(id: string) {
    const existing = document.getElementById(id);
    if (existing) document.head.removeChild(existing);
  }

  private removeAccordionStyles() {
    this.removeStyle(`accordion-header-${this.dataIn.uniqueId}`);
  }
}
